import {useEffect, useState} from 'react';
import {GoogleDriveFile} from 'types';
import close from '../ImageView/icons/close.svg';

import styles from './VideoView.module.scss';

interface Props {
  show?: boolean;
  onClose: () => void;
  videos: GoogleDriveFile[];
  selectedVideoIndex?: number;
}

function VideoView({
  show,
  onClose,
  videos = [],
  selectedVideoIndex = 0,
}: Props) {
  const [currentIndex, setCurrentIndex] = useState(selectedVideoIndex);

  // Reset to first video when modal opens
  useEffect(() => {
    if (show) {
      setCurrentIndex(selectedVideoIndex);
    }
  }, [show, selectedVideoIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        case 'Escape':
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, currentIndex, videos.length, selectedVideoIndex]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [show]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? videos.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === videos.length - 1 ? 0 : prev + 1));
  };

  if (!show || videos.length === 0) return null;

  const currentVideo = videos[currentIndex];

  const videoUrl = currentVideo.preview_url?.includes('preview')
    ? currentVideo.preview_url
    : `https://drive.google.com/file/d/${currentVideo.file_id}/preview`;
  return (
    <div className={styles.root} onClick={onClose}>
      <div className={styles.container} onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <img
          className={styles.close}
          alt="close"
          src={close}
          onClick={onClose}
        />

        {/* Navigation buttons */}
        {videos.length > 1 && (
          <>
            <button
              className={styles.navButton}
              onClick={goToPrevious}
              aria-label="Previous video"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <button
              className={styles.navButton}
              onClick={goToNext}
              aria-label="Next video"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        )}

        {/* Video container */}
        <div className={styles.videoContainer}>
          <iframe
            className={styles.video}
            src={videoUrl}
            title={currentVideo.filename}
            allow="autoplay"
            allowFullScreen
          />
        </div>

        {/* Video filename */}
        <div className={styles.filename}>{currentVideo.filename}</div>

        {/* Video counter */}
        {videos.length > 1 && (
          <div className={styles.counter}>
            {currentIndex + 1} / {videos.length}
          </div>
        )}
      </div>
    </div>
  );
}

export default VideoView;
